import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useManualQuery } from 'graphql-hooks';
import debounce from 'lodash/debounce';

import { MENTION_SUGGESTIONS } from '../graphql/user/query';
import { User } from '../types';

const DEBOUNCE = 200;
// Page size, like the app. Order: follows, followers, then everyone.
const LIMIT = 10;

// Users matching the @ being typed, asked 200ms after the last key; the next page on the scroll.
const useMentionSuggestions = () => {
  const [mentionSuggestions] = useManualQuery(MENTION_SUGGESTIONS, { skipCache: true });
  const [suggestions, setSuggestions] = useState([] as User[]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const loadingMoreRef = useRef(false);
  // Latest query: a late answer of an older one is ignored.
  const queryRef = useRef<string | null>(null);

  const fetchUsers = useMemo(() => debounce(async (search: string) => {
    const { data } = await mentionSuggestions({ variables: { search, limit: LIMIT } }) as any;
    if (queryRef.current !== search) {
      return;
    }
    const page = (data && data.mentionSuggestions) || [];
    setSuggestions(page);
    setHasMore(page.length === LIMIT);
    setLoading(false);
  }, DEBOUNCE), [mentionSuggestions]);

  const search = useCallback((query: string) => {
    queryRef.current = query;
    setLoading(true);
    fetchUsers(query);
  }, [fetchUsers]);

  const loadMore = useCallback(async () => {
    const search = queryRef.current;
    if (search === null || !hasMore || loadingMoreRef.current) {
      return;
    }
    loadingMoreRef.current = true;
    const { data } = await mentionSuggestions({ variables: { search, limit: LIMIT, offset: suggestions.length } }) as any;
    loadingMoreRef.current = false;
    if (queryRef.current !== search) {
      return;
    }
    const page: User[] = (data && data.mentionSuggestions) || [];
    setSuggestions((prev) => {
      const ids = new Set(prev.map((u) => u.id));
      return [...prev, ...page.filter((u) => !ids.has(u.id))];
    });
    setHasMore(page.length === LIMIT);
  }, [hasMore, mentionSuggestions, suggestions.length]);

  const clear = useCallback(() => {
    queryRef.current = null;
    fetchUsers.cancel();
    setSuggestions([]);
    setHasMore(false);
    setLoading(false);
  }, [fetchUsers]);

  useEffect(() => () => {
    fetchUsers.cancel();
  }, [fetchUsers]);

  return {
    suggestions,
    loading,
    search,
    loadMore,
    clear,
  };
};

export default useMentionSuggestions;
